import React, { useState, useEffect } from 'react';
import { db, doc, updateDoc, collection, addDoc, getDocs, deleteDoc, writeBatch } from '../../firebase';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/ui/DataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NOTICE_TYPES } from '../../constants/noticeTypes';

const EditNotices = () => {
  const [notices, setNotices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    imageURL: '',
    noticeType: 'GENERAL',
    enabled: false,
    lastUpdated: new Date().toISOString()
  });

  // Table columns configuration
  const columns = [
    { header: 'Title', accessor: 'title' },
    { 
      header: 'Type', 
      accessor: 'noticeType',
      Cell: ({ row }) => {
        const type = NOTICE_TYPES[row.noticeType] || NOTICE_TYPES.GENERAL;
        return (
          <span className={`px-2 py-1 rounded-full text-xs ${type.bgColor} ${type.textColor}`}>
            {type.label}
          </span>
        );
      }
    },
    {
      header: 'Status',
      accessor: 'enabled',
      Cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {row.enabled ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-blue-600 hover:text-blue-800"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-800"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'notices'));
      const noticesData = [];
      querySnapshot.forEach((doc) => {
        noticesData.push({ id: doc.id, ...doc.data() });
      });
      setNotices(noticesData);
    } catch (error) {
      toast.error('Error fetching notices: ' + error.message);
    }
  };

  const handleEdit = (notice) => {
    setFormData({
      title: notice.title,
      content: notice.content,
      imageURL: notice.imageURL || '',
      noticeType: notice.noticeType || 'GENERAL',
      enabled: notice.enabled || false,
      lastUpdated: notice.lastUpdated
    });
    setEditingId(notice.id);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        toast.error('Title and content are required');
        return;
      }

      const noticeData = {
        ...formData,
        lastUpdated: new Date().toISOString()
      };

      // If enabling this notice, disable all others
      if (noticeData.enabled) {
        const batch = writeBatch(db);
        const noticesRef = collection(db, 'notices');
        const querySnapshot = await getDocs(noticesRef);
        
        querySnapshot.forEach((doc) => {
          if (doc.id !== editingId) {
            batch.update(doc.ref, { enabled: false });
          }
        });
        
        await batch.commit();
      }

      if (isEditing) {
        await updateDoc(doc(db, 'notices', editingId), noticeData);
        toast.success('Notice updated successfully!');
      } else {
        if (noticeData.enabled) {
          // Disable all existing notices before adding new one
          const batch = writeBatch(db);
          const querySnapshot = await getDocs(collection(db, 'notices'));
          querySnapshot.forEach((doc) => {
            batch.update(doc.ref, { enabled: false });
          });
          await batch.commit();
        }
        await addDoc(collection(db, 'notices'), noticeData);
        toast.success('Notice added successfully!');
      }

      setShowModal(false);
      fetchNotices();
      resetForm();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error(`Error ${isEditing ? 'updating' : 'adding'} notice: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      try {
        await deleteDoc(doc(db, 'notices', id));
        toast.success('Notice deleted successfully!');
        fetchNotices();
      } catch (error) {
        toast.error('Error deleting notice: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      imageURL: '',
      noticeType: 'GENERAL',
      enabled: false,
      lastUpdated: new Date().toISOString()
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Manage Notices</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>Add Notice</span>
        </button>
      </div>

      <DataTable
        data={notices}
        columns={columns}
        searchable
        pagination
        itemsPerPage={10}
      />

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg mx-auto">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">
                  {isEditing ? 'Edit Notice' : 'Add New Notice'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                  rows="3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={formData.imageURL}
                  onChange={(e) => setFormData({...formData, imageURL: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notice Type
                </label>
                <select
                  value={formData.noticeType}
                  onChange={(e) => setFormData({...formData, noticeType: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                >
                  {Object.entries(NOTICE_TYPES).map(([key, value]) => (
                    <option key={key} value={key}>{value.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Enable Notice (will disable other notices)
                </label>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  {isEditing ? 'Update Notice' : 'Save Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditNotices; 